import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Column,
  ListIcon,
  LoadingState,
  Meter,
  Section,
  Table,
  cssColorFromBasedOnPercentage,
} from "@KristianIV/common-ui";
import RouteButton from "../shell/RouteButton";
import { useAppDispatch } from "../storeHooks";
import { RoundNameWithStatistics } from "../model/rounds";
import { selectDeviceAction, selectRoundAction, selectRoundNameAction } from "../round/roundReducer";
import { getStatisticsForRounds } from "../services/roundsService";
import { RoundRow, summarizeRounds, toRoundRow } from "./roundStatistics";
import styles from "./Dashboard.module.css";

const percent = (value: number) => `${Math.round(value)}%`;

const ShareCell = ({ share, count }: { share: number; count: number }) => (
  <span className={styles.nowrap}>
    {percent(share)} <span className={styles.muted}>({count.toLocaleString()})</span>
  </span>
);

const columns: Column<RoundRow>[] = [
  {
    key: "roundName",
    label: "Round",
    sortable: true,
    render: (_, row) => (
      <Link to={`/round?round-name=${row.roundName}`} className={styles.roundName}>
        {row.roundName}
      </Link>
    ),
  },
  {
    key: "correctedShare",
    label: "Corrected",
    sortable: true,
    render: (_, row) => (
      <Meter
        value={row.correctedShare}
        valueLabel={percent(row.correctedShare)}
        color={cssColorFromBasedOnPercentage(row.correctedShare)}
        size="small"
        className={styles.meter}
      />
    ),
  },
  {
    key: "sampleSetShare",
    label: "Sample set",
    sortable: true,
    align: "right",
    render: (_, row) => <ShareCell share={row.sampleSetShare} count={row.sampleSet} />,
  },
  {
    key: "ambiguousShare",
    label: "Ambiguous",
    sortable: true,
    align: "right",
    render: (_, row) => <ShareCell share={row.ambiguousShare} count={row.ambiguous} />,
  },
  {
    key: "lowAccuracyShare",
    label: "Low accuracy",
    sortable: true,
    align: "right",
    render: (_, row) => <ShareCell share={row.lowAccuracyShare} count={row.lowAccuracy} />,
  },
  {
    key: "observations",
    label: "Observations",
    sortable: true,
    align: "right",
    render: (value: number) => value.toLocaleString(),
  },
];

/** Correction progress per round, in the order the backend lists them, with the totals in the header. */
const RoundsTable = ({ jwtToken }: { jwtToken: string }) => {
  const dispatch = useAppDispatch();
  const [rounds, setRounds] = useState<RoundNameWithStatistics[] | undefined>();

  useEffect(() => {
    getStatisticsForRounds(jwtToken, (result) => {
      setRounds(result);
      // The round analyzer expects a round name selected and no round or device selected when
      // arriving from the dashboard; unchanged from the previous widget.
      if (result.length > 0) {
        dispatch(selectRoundNameAction(result[0].roundName));
      }
      dispatch(selectRoundAction(undefined));
      dispatch(selectDeviceAction(undefined));
    });
  }, [jwtToken, dispatch]);

  const rows = useMemo(() => (rounds ?? []).map(toRoundRow), [rounds]);
  const summary = useMemo(() => summarizeRounds(rounds ?? []), [rounds]);

  return (
    <Section
      title="Round analyzer"
      trailing={
        <RouteButton to="/round/overview" icon={<ListIcon />}>
          Round overview
        </RouteButton>
      }
    >
      {!rounds ? (
        <LoadingState message="Loading rounds..." />
      ) : (
        <Table
          data={rows}
          columns={columns}
          rowKey="roundName"
          search={{ placeholder: "Search rounds..." }}
          meta={`${summary.rounds} rounds · ${percent(summary.correctedShare)} corrected · ${summary.corrected.toLocaleString()} of ${summary.observations.toLocaleString()} observations`}
        />
      )}
    </Section>
  );
};

export default RoundsTable;
